import React, { useState, useEffect } from 'react';
import { X, Building, User, Mail, Phone, MapPin, Globe, ChevronDown, Bell } from 'lucide-react';
import { Owner, LeadSource, Agency } from '../../types';
import { SearchableSelect } from '../ui/SearchableSelect';
import { formatPhoneForStorage, formatPhoneForDisplay, handlePhoneInputChange } from '../../lib/phoneUtils';

interface AddOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (owner: Owner) => void;
  agencies?: Agency[];
  ownerToEdit?: Owner | null;
}

export const AddOwnerModal: React.FC<AddOwnerModalProps> = ({ isOpen, onClose, onSave, agencies = [], ownerToEdit }) => {
  const [formData, setFormData] = useState<Partial<Owner>>({
    civility: 'Monsieur',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    zipCode: '',
    email: '',
    phone: '',
    source: 'Site Web',
    agencyId: ''
  });

  const [suggestions, setSuggestions] = useState<any[]>([]);

  // Reset or Populate form when opening
  useEffect(() => {
    if (isOpen) {
        if (ownerToEdit) {
            setFormData({
                id: ownerToEdit.id,
                civility: ownerToEdit.civility || 'Monsieur',
                firstName: ownerToEdit.firstName,
                lastName: ownerToEdit.lastName,
                address: ownerToEdit.address || '',
                city: ownerToEdit.city || '',
                zipCode: ownerToEdit.zipCode || '',
                email: ownerToEdit.email,
                phone: formatPhoneForDisplay(ownerToEdit.phone || ''),
                source: ownerToEdit.source || 'Site Web',
                agencyId: ownerToEdit.agencyId || '',
                newsletterOptin: ownerToEdit.newsletterOptin || false
            });
        } else {
            setFormData({
                civility: 'Monsieur',
                firstName: '',
                lastName: '',
                address: '',
                city: '',
                zipCode: '',
                email: '',
                phone: '',
                source: 'Site Web',
                agencyId: '',
                newsletterOptin: false
            });
        }
        setSuggestions([]);
    }
  }, [isOpen, ownerToEdit]);

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = handlePhoneInputChange(e.target.value);
      setFormData({...formData, phone: val});
  };

  const handlePhoneBlur = () => {
      if (formData.phone) {
          const formatted = formatPhoneForStorage(formData.phone);
          setFormData({ ...formData, phone: formatted });
      }
  };

  const handleAddressSearch = async (query: string) => {
      setFormData({ ...formData, address: query });
      if (query.length > 3) {
          try {
              const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
              const data = await res.json();
              setSuggestions(data.features || []);
          } catch (e) {
              console.error("Address API Error", e);
          }
      } else {
          setSuggestions([]);
      }
  };

  const selectAddress = (feature: any) => {
      setFormData({ 
          ...formData, 
          address: feature.properties.name || feature.properties.label,
          city: feature.properties.city,
          zipCode: feature.properties.postcode
      });
      setSuggestions([]);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (formData.firstName && formData.lastName) {
        onSave({
            ...formData,
            // Formatage final pour l'enregistrement
            phone: formData.phone ? formatPhoneForStorage(formData.phone) : ''
        } as Owner);
        onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* HEADER */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20">
          <div>
              <h3 className="text-2xl font-bold text-slate-900 flex items-center">
                <div className="bg-amber-100 p-2 rounded-lg mr-3">
                    <User className="w-6 h-6 text-amber-600" />
                </div>
                {ownerToEdit ? 'Modifier le Propriétaire' : 'Nouveau Propriétaire'}
              </h3>
              <p className="text-slate-500 text-sm mt-1 ml-14">
                {ownerToEdit ? 'Mettre à jour les informations du propriétaire' : 'Ajouter un nouveau propriétaire au CRM'}
              </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 overflow-y-auto flex-1 bg-slate-50/50">
            <form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
                
                {/* 1. INFORMATIONS PERSONNELLES */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">1</span>
                        Informations Personnelles
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-1">
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Civilité</label>
                            <SearchableSelect
                                options={[
                                    { value: 'Monsieur', label: 'Monsieur' },
                                    { value: 'Madame', label: 'Madame' }
                                ]}
                                value={formData.civility || ''}
                                onChange={(value) => setFormData({...formData, civility: value as any})}
                                placeholder="Sélectionner une civilité"
                                icon={<User className="w-4 h-4" />}
                                searchPlaceholder="Rechercher une civilité..."
                                showAllOnOpen={true}
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Prénom</label>
                            <div className="relative">
                                <User className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                <input
                                    required
                                    type="text"
                                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                                    placeholder="Jean"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                />
                            </div>
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Nom</label>
                            <input
                                required
                                type="text"
                                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                                placeholder="Dupont"
                                value={formData.lastName}
                                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                            />
                        </div>
                    </div>
                </div>

                {/* 2. COORDONNÉES */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">2</span>
                        Coordonnées
                    </h4>
                    
                    <div className="space-y-4">
                        <div className="relative">
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Adresse postale</label>
                            <MapPin className="absolute left-4 top-9 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                className="w-full pl-12 p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                                placeholder="12 rue de la Paix, 75000 Paris"
                                value={formData.address}
                                onChange={(e) => handleAddressSearch(e.target.value)}
                            />
                            {suggestions.length > 0 && (
                                <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-auto">
                                    {suggestions.map((s, i) => (
                                        <li key={i} onClick={() => selectAddress(s)} className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                                            <span className="font-bold block">{s.properties.name}</span>
                                            <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <input 
                                    type="text" 
                                    placeholder="Code Postal" 
                                    readOnly 
                                    className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500" 
                                    value={formData.zipCode || ''} 
                                />
                                <input 
                                    type="text" 
                                    placeholder="Ville" 
                                    readOnly 
                                    className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500" 
                                    value={formData.city || ''} 
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                <input
                                    type="email"
                                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900"
                                    placeholder="email@exemple.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Téléphone</label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                <input
                                    type="tel"
                                    placeholder="06 12 34 56 78"
                                    className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none font-mono bg-white text-slate-900"
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    onBlur={handlePhoneBlur}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. INFORMATIONS SUPPLÉMENTAIRES */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">3</span>
                        Informations Supplémentaires
                    </h4>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Source du contact</label>
                            <SearchableSelect
                                options={['Site Web', 'Appel Entrant', 'Airbnb', 'Leboncoin', 'Recommandation', 'Réseau', 'Autre'].map(s => ({
                                    value: s,
                                    label: s
                                }))}
                                value={formData.source || ''}
                                onChange={(value) => setFormData({...formData, source: value as LeadSource})}
                                placeholder="Sélectionner une source"
                                icon={<Globe className="w-4 h-4" />}
                                searchPlaceholder="Rechercher une source..."
                                showAllOnOpen={true}
                            />
                        </div>

                        <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <input
                                type="checkbox"
                                id="newsletter-optin"
                                checked={formData.newsletterOptin || false}
                                onChange={(e) => setFormData({...formData, newsletterOptin: e.target.checked})}
                                className="mt-1 w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500 focus:ring-2"
                            />
                            <div className="flex-1">
                                <label htmlFor="newsletter-optin" className="text-sm font-semibold text-slate-900 cursor-pointer flex items-center">
                                    <Bell className="w-4 h-4 mr-2 text-amber-600" />
                                    S'inscrire à la newsletter Placid
                                </label>
                                <p className="text-xs text-slate-600 mt-1">
                                    Cochez cette case pour permettre au propriétaire de recevoir la newsletter Placid
                                </p>
                            </div>
                        </div>

                        {agencies.length > 0 && (
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Agence (Optionnel si Global)</label>
                                <SearchableSelect
                                    options={[
                                        { value: '', label: 'Aucune agence' },
                                        ...agencies.map(a => ({
                                            value: a.id,
                                            label: `${a.city} (${a.name})`,
                                            subtitle: a.name
                                        }))
                                    ]}
                                    value={formData.agencyId || ''}
                                    onChange={(value) => setFormData({...formData, agencyId: value})}
                                    placeholder="Sélectionner une agence..."
                                    icon={<Building className="w-4 h-4" />}
                                    searchPlaceholder="Rechercher une agence..."
                                    allowClear
                                />
                            </div>
                        )}
                    </div>
                </div>

            </form>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="p-6 border-t border-slate-100 bg-white sticky bottom-0 z-20 flex justify-end space-x-4">
            <button 
                onClick={onClose} 
                className="px-6 py-3 text-slate-500 hover:bg-slate-100 rounded-xl font-bold transition-colors"
            >
                Annuler
            </button>
            <button 
                onClick={handleSubmit} 
                disabled={!formData.firstName || !formData.lastName}
                className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold shadow-lg shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center"
            >
                {ownerToEdit ? 'Enregistrer les modifications' : 'Créer le propriétaire'}
            </button>
        </div>

      </div>
    </div>
  );
};
